import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { iif, Observable, of } from 'rxjs';
import { Injectable } from '@angular/core';
import { selectSession, SessionActions } from '@state/session';
import { Store } from '@ngrx/store';
import { mergeMap, switchMap, take } from 'rxjs/operators';
import { Actions, ofType } from '@ngrx/effects';
import { ACCOUNT_TYPE, CARGOCHIEF_KEYS } from '@domain/constants/app-variables';
import { environment } from '@environment/environment';
import { DateTime } from 'luxon';
import { Cargochief } from '@service/cargochief/cargochief.service';

@Injectable({
	providedIn: 'root',
})
export class AuthGuardV2Service  {
	constructor(
		private store: Store,
		private actions$: Actions<any>,
		private router: Router,
		private cargoChief: Cargochief
	) {}

	verifyAccess(
		routeAccountTypes: Array<string>,
		routeRoleTypes: Array<string>
	): Observable<boolean | UrlTree> {
		return this.store.select(selectSession).pipe(
			take(1),
			switchMap((session) =>
				iif(
					() => {
						const isCreditCardUser = session.data.account.creditCardPayment !== false;
						const isBillableAccountType = [
							ACCOUNT_TYPE.BROKER,
							ACCOUNT_TYPE.SHIPPER,
							ACCOUNT_TYPE.CARRIER,
						].includes(session.data.account.accountType.toUpperCase());

						// set to nextPaymentDate or invalid date in past
						const nextPaymentDate = session.data.account.nextPaymentDate
							? DateTime.fromMillis(Number(session.data.account.nextPaymentDate))
							: null;
						const isNextPaymentDatePassed =
							!nextPaymentDate ||
							(nextPaymentDate &&
								nextPaymentDate.plus({ day: environment.PAYMENT_TRANSIT_DAYS }).diffNow()
									.milliseconds < 0);
						const isPaidPlan = session.data.account.planAmount > 0;

						const needsToPay =
							isCreditCardUser && isPaidPlan && isBillableAccountType && isNextPaymentDatePassed;

						if (needsToPay) {
							this.cargoChief.setItem({
								[CARGOCHIEF_KEYS.PAYMENT_AMOUNT]: session.data.account.planAmount,
							});
						}

						return !routeRoleTypes.includes('PAYMENT') && needsToPay;
					},
					of(this.router.createUrlTree(['/register/complete_payment'])),
					of(
						routeAccountTypes.includes('ALL') ||
							routeAccountTypes.includes(session.data.account.accountType.toUpperCase())
							? true
							: this.router.createUrlTree([session.data.user.landingPage])
					)
				)
			)
		);
	}

	validateSession(session, route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
		this.store.dispatch(SessionActions.validate.start({ payload: { route, redirect: false } }));
		return this.actions$.pipe(
			ofType(SessionActions.login.setup.success, SessionActions.login.setup.failed),
			take(1),
			mergeMap((action) =>
				action.type === SessionActions.login.setup.success.type
					? this.verifyAccess(route.data.type, route.data.role)
					: of(this.router.createUrlTree(['/login']))
			)
		);
	}

	canActivate(
		route: ActivatedRouteSnapshot,
		state: RouterStateSnapshot
	): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
		if (!route?.data?.type || route?.data?.type?.length === 0) {
			return true;
		}

		return this.store.select(selectSession).pipe(
			take(1),
			mergeMap((session) =>
				!session?.data ||
				!session?.nextUpdate ||
				DateTime.fromISO(session.nextUpdate).diffNow('minutes').minutes <= 0
					? this.validateSession(session, route)
					: this.verifyAccess(route.data.type, route.data.role)
			)
		);
	}
}
